import { PermissionKey, hasPermissionV2 } from '@c4ytechnology/authz';
import type {
  AdminAccessReview,
  AdminAccessReviewItem,
  AdminConsoleAccess,
  AdminReviewOutcome,
} from '../api/adminConsole';

// Access reviews (RBAC Phase 2 item 8): pure rules mirrored from admin-api so the UI hides what the
// server would refuse. The server stays authoritative.

type Viewer = { id: string; access?: AdminConsoleAccess | null } | null | undefined;

export type ItemDecidability = { allowed: true } | { allowed: false; reason: string };

/** Keep/revoke is for the item's reviewer only, never on your own assignment, and only while open. */
export function itemDecidability(
  viewer: Viewer,
  review: Pick<AdminAccessReview, 'status'>,
  item: Pick<AdminAccessReviewItem, 'userId' | 'reviewerUserId'>,
): ItemDecidability {
  if (!viewer) return { allowed: false, reason: 'Sign in to review access.' };
  if (review.status !== 'open') return { allowed: false, reason: 'This review is closed.' };
  if (!hasPermissionV2(viewer.access, PermissionKey.IAM_REVIEWS_DECIDE)) {
    return { allowed: false, reason: 'Needs iam.reviews.decide.' };
  }
  if (item.userId === viewer.id) return { allowed: false, reason: 'You cannot review your own access.' };
  if (item.reviewerUserId !== viewer.id) return { allowed: false, reason: 'Only the assigned reviewer can decide.' };
  return { allowed: true };
}

/** Reassigning needs manage, an open review, and a reviewer other than the item's user. */
export function canReassignItem(
  viewer: Viewer,
  review: Pick<AdminAccessReview, 'status'>,
): boolean {
  return (
    !!viewer &&
    review.status === 'open' &&
    hasPermissionV2(viewer.access, PermissionKey.IAM_REVIEWS_MANAGE)
  );
}

export function isValidReassignTarget(item: Pick<AdminAccessReviewItem, 'userId'>, reviewerUserId: string): boolean {
  return !!reviewerUserId && reviewerUserId !== item.userId;
}

export type CloseSummary = {
  total: number;
  keep: number;
  revoke: number;
  undecided: number;
  /** Assignments the close will try to revoke (revoke decisions, plus undecided under auto-revoke). */
  willRevoke: number;
};

/** What closing will do, for the confirmation dialog. */
export function closeReviewSummary(
  review: Pick<AdminAccessReview, 'autoRevoke'>,
  items: ReadonlyArray<Pick<AdminAccessReviewItem, 'decision'>>,
): CloseSummary {
  let keep = 0;
  let revoke = 0;
  let undecided = 0;
  for (const i of items) {
    if (i.decision === 'keep') keep += 1;
    else if (i.decision === 'revoke') revoke += 1;
    else undecided += 1;
  }
  return {
    total: items.length,
    keep,
    revoke,
    undecided,
    willRevoke: revoke + (review.autoRevoke ? undecided : 0),
  };
}

/** Same summary from the review's server counts (no need to load every item). */
export function closeSummaryFromCounts(review: Pick<AdminAccessReview, 'autoRevoke' | 'counts'>): CloseSummary {
  const { total, keep, revoke, undecided } = review.counts;
  return { total, keep, revoke, undecided, willRevoke: revoke + (review.autoRevoke ? undecided : 0) };
}

export function closeSummaryText(summary: CloseSummary, autoRevoke: boolean): string {
  const parts = [`${summary.revoke} revoke decision${summary.revoke === 1 ? '' : 's'} will be applied`];
  if (summary.undecided > 0) {
    parts.push(
      autoRevoke
        ? `${summary.undecided} undecided item${summary.undecided === 1 ? '' : 's'} will also be revoked (auto-revoke is on)`
        : `${summary.undecided} undecided item${summary.undecided === 1 ? '' : 's'} will be left in place and recorded as undecided`,
    );
  }
  parts.push(`${summary.keep} kept`);
  return `${parts.join('; ')}. Revokes run through the normal guardrails, so some may fail.`;
}

export function outcomeChip(outcome: AdminReviewOutcome | null): {
  label: string;
  color: 'default' | 'success' | 'warning' | 'error';
} | null {
  switch (outcome) {
    case 'kept':
      return { label: 'Kept', color: 'success' };
    case 'revoked':
      return { label: 'Revoked', color: 'default' };
    case 'undecided':
      return { label: 'Undecided', color: 'warning' };
    case 'revoke_failed':
      return { label: 'Revoke failed', color: 'error' };
    default:
      return null;
  }
}

/** Progress label, e.g. "3 of 10 decided". */
export function reviewProgressLabel(counts: AdminAccessReview['counts']): string {
  const decided = counts.keep + counts.revoke;
  return `${decided} of ${counts.total} decided`;
}

/** True when the review is open and its due date has passed. */
export function isReviewOverdue(review: Pick<AdminAccessReview, 'status' | 'dueAt'>, now: number = Date.now()): boolean {
  if (review.status !== 'open') return false;
  const ms = Date.parse(review.dueAt);
  return !Number.isNaN(ms) && ms < now;
}
